import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import { v4 as uuid4 } from 'uuid'

import { GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { FileCategory, ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import {
  DefaultVariableResult,
  getActionLabel,
  IActionRepeaterValue,
  MAKYO_STORAGE_PATH,
  VariableActionOption,
  VariableResultAvatar,
  VariableResultColumn,
  VariableResultColumnData,
  VariableResultCommon,
  VariableResultCurrentListItem,
  VariableResultCustomText,
  VariableResultGlobalVariable,
  VariableResultIcon,
  VariableResultProfileData,
  VariableResultSessionID,
  VariableResultSingleCell,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { ArrowShortRight16, Db16, SpaceBar24, Table16, User16 } from '@makyoapp/makyo-icon/icon'
import * as Mime from '@makyoapp/makyo-icon/mimetype'
import { Unknown } from '@makyoapp/makyo-icon/mimetype'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import {
  ActionTypeIconMap,
  Button,
  Chip,
  ChipProps,
  DateTimeInput,
  Modal,
  ModalGridFooter,
  SimpleInput,
} from '@makyoapp/makyo-ui'

import { MarkdownEditor } from '@/lib/markdown'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { THUMB_SQUARE } from '@/shared/constants'
import { appIconKeys } from '@/shared/icons/constants'
import { getFileTypeFromId, getTableColumnsWithRowId } from '@/shared/utils'

import { useDbContext } from '../../../context/DbContext'
import { ImageManagerKind } from '../../../context/ImageManagerContext'
import { ColumnTypeIcon } from '../../ColumnTypeIcon'
import { FileManager } from '../../FileManager/FileManager'
import { getMimeIcon } from '../../FileManager/MyFilesPanel'
import { ImageManager } from '../../ImageManager/ImageManager'
import { VariableAvatarBody } from '../VariableAvatar'
import { VariableColumnBody } from '../VariableColumn'
import { VariableColumnDataBody } from '../VariableColumnData'
import { VariableCurrentListItemBody } from '../VariableCurrentListItem'
import { getDateTimeLabel } from '../VariableDateTime'
import { VariableIconBody } from '../VariableIcon'
import { VariableProfileDataBody } from '../VariableProfileData'
import { useVariableSelectContext } from '../VariableSelectContext'
import { VariableSingleCellBody } from '../VariableSingleCell'
import ChipCurrentRow from './ChipCurrentRow'
import ChipMenu from './ChipMenu'
import { ChipTextInput } from './ChipTextInput'
import { DragHandler, EditDD } from './components'

function ChipImage({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultCommon
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { value: contextValue, onChange, multiple } = useVariableSelectContext()
  const [show, setShow] = useState(false)

  const metadataLink = !value.value.includes(MAKYO_STORAGE_PATH)
    ? value.value
    : GetImageMetdataLink(value.value, ImageMetadata.XS)

  const id =
    (multiple ? (contextValue as DefaultVariableResult[])[index].id : (contextValue as DefaultVariableResult).id) ??
    uuid4()

  return (
    <Chip {...chipProps}>
      <DragHandler listeners={listeners} />
      <img
        alt=''
        loading='lazy'
        src={metadataLink}
        className={clsx('w-[14px] h-[14px] object-cover')}
        onError={(e) => {
          e.currentTarget.src = THUMB_SQUARE
        }}
      />
      <div className='w-full'>
        <Trans>Image</Trans>
      </div>
      <EditDD show={show} onToggle={setShow} menuClassName='min-w-[300px] max-w-[500px]'>
        <ImageManager
          id={`var-select-${index}-img-mgr`}
          smallPreview
          maxHeight='36vh'
          forceUrlPanelHeight
          initialValue={{ url: value.value }}
          onChange={(ty, v) => {
            let nextVal: VariableResultCommon
            switch (ty) {
              case ImageManagerKind.StockPhotos:
                nextVal = {
                  id,
                  flag: VariableSelectFeatureFlag.Image,
                  value: `${v}?q=50&w=720`,
                }
                break
              case ImageManagerKind.Upload:
              case ImageManagerKind.ImageUrl:
              case ImageManagerKind.OpenAI:
              default:
                nextVal = {
                  id,
                  flag: VariableSelectFeatureFlag.Image,
                  value: `${v}`,
                }
                break
            }
            const next = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : { ...contextValue, value: nextVal.value }

            if (multiple && Array.isArray(next)) {
              ;(next as DefaultVariableResult[])[index] = nextVal
            }
            onChange(next as DefaultVariableResult[])
            setShow(false)
          }}
          modal={false}
        />
      </EditDD>
    </Chip>
  )
}

function ChipPageCover({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultCommon
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  return (
    <Chip {...chipProps}>
      <DragHandler listeners={listeners} />
      <img alt='' loading='lazy' src={value.value} className={clsx('w-[14px] h-[14px] object-cover')} />
      <div className='w-full'>
        <Trans>Page Cover</Trans>
      </div>
    </Chip>
  )
}

function ChipFile({
  value,
  index,
  chipProps: { listeners, ...chipProps },
  fileTypes,
}: {
  value: VariableResultCommon
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
  fileTypes?: FileCategory[]
}) {
  const { value: contextValue, onChange, multiple } = useVariableSelectContext()
  const [show, setShow] = useState(false)

  const metadataLink = value.value

  const id =
    (multiple ? (contextValue as DefaultVariableResult[])[index].id : (contextValue as DefaultVariableResult).id) ??
    uuid4()

  const mime = getMimeIcon(metadataLink)
  const Icon = Mime[mime]

  return (
    <Chip {...chipProps}>
      <DragHandler listeners={listeners} />
      {Icon ? (
        <Icon width={16} height={16} className='shrink-0' />
      ) : (
        <Unknown width={16} height={16} className='shrink-0' />
      )}
      <div className='w-full'>
        <Trans>File</Trans>
      </div>
      <EditDD show={show} onToggle={setShow} menuClassName='min-w-[300px] max-w-[500px]'>
        <FileManager
          fileTypes={fileTypes}
          id={`var-select-${index}-file-mgr`}
          maxHeight='20vh'
          gridCols={3}
          forceUrlPanelHeight
          onChange={(ty, v) => {
            const nextVal: VariableResultCommon = {
              id,
              flag: VariableSelectFeatureFlag.File,
              value: `${v}`,
            }

            const next = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : { ...contextValue, value: nextVal.value }

            if (multiple && Array.isArray(next)) {
              ;(next as DefaultVariableResult[])[index] = nextVal
            }
            onChange(next as DefaultVariableResult[])
            setShow(false)
          }}
          modal={false}
        />
      </EditDD>
    </Chip>
  )
}

function ChipRichText({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultCommon
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { t } = useTranslation()
  const { value: contextValue, onChange, multiple } = useVariableSelectContext()
  const [show, setShow] = useState(false)
  const [tempValue, setTempValue] = useState(value.value)

  const id =
    (multiple ? (contextValue as DefaultVariableResult[])[index].id : (contextValue as DefaultVariableResult).id) ??
    uuid4()

  useEffect(() => {
    if (value.value) {
      setTempValue(value.value)
    }
  }, [setTempValue, value.value])

  useEffect(() => {
    if (!show && value.value !== tempValue) {
      setTempValue(value.value)
    }
  }, [show, tempValue, value.value])

  return (
    <Chip {...chipProps}>
      <DragHandler listeners={listeners} />
      <div className='w-full'>
        <Trans>Rich Text</Trans>
      </div>
      <EditDD show={show} onToggle={setShow} menuClassName='min-w-[300px] max-w-[60vw] z-10000'>
        <div className='min-h-[400px] min-w-[700px] flex-cc'>
          <MarkdownEditor
            value={tempValue}
            height='400px'
            onChange={(newValue) => {
              setTempValue(newValue ?? '')
            }}
            className='w-full'
          />
        </div>
        <ModalGridFooter
          onHide={() => {
            // setShowModal(false)
          }}
          onSubmit={() => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : { ...contextValue, value: tempValue }

            if (multiple && Array.isArray(nextVal)) {
              ;(nextVal as DefaultVariableResult[])[index] = {
                id,
                flag: VariableSelectFeatureFlag.RichText,
                value: tempValue,
              }
            }
            onChange(nextVal as DefaultVariableResult[])
            setShow(false)
          }}
          cancelLabel={t('Cancel')}
          onCancel={() => {
            setShow(false)
          }}
        />
        {/* <Modal.Footer justify={JustifyVariant.End}>
          <Button
            onClick={() => {
              const nextVal = multiple
                ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
                : { ...contextValue, value: tempValue }

              if (multiple && Array.isArray(nextVal)) {
                ;(nextVal as DefaultVariableResult[])[index] = {
                  id,
                  flag: VariableSelectFeatureFlag.RichText,
                  value: tempValue,
                }
              }
              onChange(nextVal as DefaultVariableResult[])
              setShow(false)
            }}
          >
            <Trans>Done</Trans>
          </Button>
        </Modal.Footer> */}
      </EditDD>
    </Chip>
  )
}

function ChipSpace({
  chipProps: { listeners, ...chipProps },
}: {
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  return (
    <Chip {...chipProps}>
      <DragHandler listeners={listeners} />
      <div className='w-full'>
        <SpaceBar24 width={16} height={16} />
      </div>
    </Chip>
  )
}

function ChipCustomText({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultCustomText
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const [show, setShow] = useState(false)
  const [tempValue, setTempValue] = useState(value.value)
  const { value: contextValue, onChange, variableCustomTextOptions, disableInput } = useVariableSelectContext()

  const { inputType = 'text', validation } = variableCustomTextOptions

  useEffect(() => {
    if (value.value) {
      setTempValue(value.value)
    }
  }, [setTempValue, value.value])

  useEffect(() => {
    if (!show && value.value !== tempValue) {
      setTempValue(value.value)
    }
  }, [show, tempValue, value.value])

  const id = (contextValue as DefaultVariableResult[])[index].id

  return (
    <Chip {...chipProps}>
      <DragHandler listeners={listeners} />
      <div className='w-full txt'>{value.value}</div>
      <EditDD show={show} onToggle={setShow}>
        <div className='p-2 flex flex-col gap-2'>
          <SimpleInput
            disabled={disableInput}
            type={inputType}
            value={tempValue}
            onChange={(e) => {
              const val = e.currentTarget.value
              if (inputType === 'number') {
                const numVal = isNaN(Number(val)) ? (tempValue ?? 0) : val
                setTempValue(Number(numVal))
              } else {
                setTempValue(val)
              }
            }}
          />
          <Button
            onClick={() => {
              const nextVal = [...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[]
              nextVal[index] = {
                id,
                flag: VariableSelectFeatureFlag.CustomText,
                value: tempValue,
              }
              onChange(nextVal)
              setShow(false)
            }}
          >
            <Trans>Done</Trans>
          </Button>
        </div>
      </EditDD>
    </Chip>
  )
}

function ChipColumnData({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultColumnData
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const {
    value: contextValue,
    onChange,
    multiple,
    variableColumnOptions,
    forceUserSpecific,
  } = useVariableSelectContext()
  const { tables, dbId } = useDbClientContext()

  const [show, setShow] = useState(false)
  const [us, setUs] = useState(forceUserSpecific ? forceUserSpecific : value.userSpecific)

  useEffect(() => {
    if (!show && !forceUserSpecific && value.userSpecific !== us) {
      setUs(value.userSpecific)
    }
  }, [show, forceUserSpecific, us, value.userSpecific, setUs])

  const { allItems } = useDbContext()
  //const { dbId } = useVariableSelectContext()
  const dbObj = allItems?.find((d) => d.id === dbId)
  const tblObj = tables.find(({ id }) => id === value?.tbl)
  const colObj = tblObj?.columns.find((c) => c.id === value?.col)

  return (
    <Chip
      {...chipProps}
      //className={clsx(chipProps.className, multiple && 'w-[140px]! txt')}
      tipClassName='text-m-sm'
      portalTip
      title={
        <div className='flex-c'>
          <div className='text-center whitespace-nowrap'>
            <Trans>Column Data</Trans>
          </div>
          <div className='flex-c'>
            <div className='flex-c'>
              <Db16 />
              {dbObj?.name}
            </div>
            {tblObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <Table16 />
                  {tblObj.name}
                </div>
              </>
            )}
            {colObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <ColumnTypeIcon type={colObj.type} />
                  {colObj.name}
                </div>
              </>
            )}
          </div>
        </div>
      }
    >
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>{colObj?.name || 'Col. Data'}</div>
      <EditDD show={show} onToggle={setShow} menuWrapperClassName='py-2'>
        <VariableColumnDataBody
          setShow={setShow}
          columnTypes={variableColumnOptions.columnTypes}
          value={
            multiple
              ? ((contextValue as [])[index] as VariableResultColumnData)
              : (contextValue as VariableResultColumnData)
          }
          onChange={(next) => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : next

            if (multiple) {
              ;(nextVal as DefaultVariableResult[])[index] = next
            }

            onChange(nextVal)
          }}
          setUserSpecific={setUs}
          userSpecific={us}
        />
      </EditDD>
    </Chip>
  )
}

function ChipSingleCell({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultSingleCell
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { value: contextValue, onChange, multiple, variableColumnOptions } = useVariableSelectContext()
  const { tables, db } = useDbClientContext()

  const [show, setShow] = useState(false)
  const [us, setUs] = useState(value.userSpecific)
  const [rowNum, setRowNum] = useState(value.row)

  const dbObj = db
  const tblObj = tables.find(({ id }) => id === value?.tbl)

  const tableColumns = getTableColumnsWithRowId(tblObj?.columns || [])
  const colObj = tableColumns.find((c) => c.id === value?.col)

  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      portalTip
      title={
        <div className='flex-c'>
          <div className='text-center whitespace-nowrap'>
            <Trans>Single Cell Value</Trans>
          </div>
          <div className='flex-c'>
            <div className='flex-c'>
              <Db16 />
              {dbObj?.name}
            </div>
            {tblObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <Table16 />
                  {tblObj.name}
                </div>
              </>
            )}
            {colObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <ColumnTypeIcon type={colObj.type} />
                  {colObj.name} ({value.row})
                </div>
              </>
            )}
          </div>
        </div>
      }
    >
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>
        <Trans>Cell Value</Trans>
      </div>
      <EditDD
        show={show}
        onToggle={(nextShow) => {
          if (!nextShow) {
            if (value.userSpecific !== us) {
              setUs(value.userSpecific)
            }
            if (value.row !== rowNum) {
              setRowNum(value.row)
            }
          }
          setShow(nextShow)
        }}
        menuWrapperClassName='py-2'
      >
        <VariableSingleCellBody
          setShow={setShow}
          rowNum={rowNum}
          setRowNum={setRowNum}
          columnTypes={variableColumnOptions.columnTypes}
          value={
            multiple
              ? ((contextValue as [])[index] as VariableResultSingleCell)
              : (contextValue as VariableResultSingleCell)
          }
          onChange={(next) => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : next

            if (multiple) {
              ;(nextVal as DefaultVariableResult[])[index] = next
            }

            onChange(nextVal)
          }}
          setUserSpecific={setUs}
          userSpecific={us}
          isSingleCell={true}
        />
      </EditDD>
    </Chip>
  )
}

function ChipColumn({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultColumn
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { value: contextValue, onChange, multiple, variableColumnOptions, tableId } = useVariableSelectContext()
  const { tables, dbId } = useDbClientContext()
  const [show, setShow] = useState(false)

  const { allItems } = useDbContext()
  const dbObj = allItems?.find((d) => d.id === dbId)
  const tblObj = tables.find(({ id }) => id === tableId)
  const colObj = tblObj?.columns.find((c) => c.id === value?.col)

  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      portalTip
      title={
        <div className='flex-c'>
          <div className='text-center whitespace-nowrap'>
            <Trans>Column</Trans>
          </div>
          <div className='flex-c'>
            <div className='flex-c'>
              <Db16 />
              {dbObj?.name}
            </div>
            {tblObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <Table16 />
                  {tblObj.name}
                </div>
              </>
            )}
            {colObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <ColumnTypeIcon type={colObj.type} />
                  {colObj.name}
                </div>
              </>
            )}
          </div>
        </div>
      }
    >
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>{colObj?.name || <Trans>Column</Trans>}</div>
      <EditDD
        show={show}
        onToggle={(nextShow) => {
          setShow(nextShow)
        }}
        menuWrapperClassName='py-2'
      >
        <VariableColumnBody
          tableId={tableId}
          setShow={setShow}
          columnTypes={variableColumnOptions.columnTypes}
          value={
            multiple ? ((contextValue as [])[index] as VariableResultColumn) : (contextValue as VariableResultColumn)
          }
          onChange={(next) => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : next

            if (multiple) {
              ;(nextVal as DefaultVariableResult[])[index] = next
            }

            onChange(nextVal)
          }}
        />
      </EditDD>
    </Chip>
  )
}

function ChipProfileData({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultProfileData
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { value: contextValue, onChange, multiple } = useVariableSelectContext()
  const { tables } = useDbClientContext()
  const { currentUser } = useAppAuthContext()

  const [show, setShow] = useState(false)

  const userTbl = tables.find((tb) => tb.id === window.appUserTableID)
  const tableColumns = getTableColumnsWithRowId(userTbl?.columns || [])
  const colObj = tableColumns.find((c) => c.id === value?.col)
  const invalidUserTable = window.appUserTableID !== userTbl?.id || !colObj

  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      portalTip
      title={
        invalidUserTable ? (
          <div className='flex-c red'>
            <MaterialIcon size={18} icon='error' fill />
            <Trans>Invalid profile data due to user table change.</Trans>
          </div>
        ) : (
          <div>
            <div className='flex-c'>
              <div className='text-center whitespace-nowrap'>
                <Trans>Profile Data</Trans>
              </div>
              <div className='flex-c'>
                {colObj && (
                  <>
                    <ArrowShortRight16 />
                    <div className='flex-c whitespace-nowrap'>
                      <ColumnTypeIcon type={colObj.type} />
                      {colObj.name}
                    </div>
                  </>
                )}
              </div>
            </div>

            {currentUser && (
              <div className='mt-2 flex-c'>
                <div className='flex items-center gap-1 whitespace-nowrap font-medium text-brand-500 dark:text-brand-700'>
                  <MaterialIcon icon='account_circle' size={16} />
                  {currentUser.email}
                </div>
              </div>
            )}
          </div>
        )
      }
    >
      <DragHandler listeners={listeners} />
      {invalidUserTable && <MaterialIcon size={16} fill icon='error' className='red' />}
      <User16 className='shrink-0' />
      <div className='flex-c txt w-full'>{colObj?.name || <Trans>Profile Data</Trans>}</div>
      <EditDD
        show={show}
        onToggle={(nextShow) => {
          setShow(nextShow)
        }}
        menuWrapperClassName='py-2'
      >
        <VariableProfileDataBody
          setShow={setShow}
          value={value}
          onChange={(next) => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : next

            if (multiple) {
              ;(nextVal as DefaultVariableResult[])[index] = next
            }

            onChange(nextVal)
          }}
        />
      </EditDD>
    </Chip>
  )
}

function ChipSessionID({
  chipProps: { listeners, ...chipProps },
}: {
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  return (
    <Chip {...chipProps} tipClassName='text-m-sm'>
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>
        <MaterialIcon icon={appIconKeys.tableSession} size={14} />
        <Trans>Session ID</Trans>
      </div>
    </Chip>
  )
}

function ChipUniqueID({
  chipProps: { listeners, ...chipProps },
}: {
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  return (
    <Chip {...chipProps} tipClassName='text-m-sm'>
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>
        <MaterialIcon icon={appIconKeys.uniqueId} size={14} />
        <Trans>Unique Identifier</Trans>
      </div>
    </Chip>
  )
}

function ChipGlobalVariable({
  value,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultGlobalVariable
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      title={
        <div className='flex-c'>
          <div className='flex-c'>
            <MaterialIcon size={16} icon={appIconKeys.globalVariable} />
            <Trans>Global Variable</Trans>
          </div>

          <ArrowShortRight16 />

          <div className='flex-c'>
            <MaterialIcon size={16} icon={appIconKeys.storage} />
            {value.value}
          </div>
        </div>
      }
    >
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>
        <MaterialIcon icon={appIconKeys.globalVariable} size={14} />
        <Trans>{value.value}</Trans>
      </div>
    </Chip>
  )
}

function ChipCurrentListItem({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultCurrentListItem
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { value: contextValue, onChange, multiple, variableColumnOptions, tableId } = useVariableSelectContext()
  const { allItems } = useDbContext()
  const { tables, dbId } = useDbClientContext()
  const [show, setShow] = useState(false)
  const [us, setUs] = useState(value.userSpecific)

  const dbObj = allItems?.find((d) => d.id === dbId)
  const tblObj = tables.find(({ id }) => id === tableId)
  const colObj = tblObj?.columns.find((c) => c.id === value?.col)

  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      portalTip
      title={
        <div className='flex-c'>
          <div className='text-center whitespace-nowrap'>
            <Trans>Current List Item</Trans>
          </div>
          <div className='flex-c'>
            <div className='flex-c'>
              <Db16 />
              {dbObj?.name}
            </div>
            {tblObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <Table16 />
                  {tblObj.name}
                </div>
              </>
            )}
            {colObj && (
              <>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <ColumnTypeIcon type={colObj.type} />
                  {colObj.name}
                </div>
              </>
            )}
          </div>
        </div>
      }
    >
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>{colObj?.name || <Trans>Current List Item</Trans>}</div>
      <EditDD
        show={show}
        onToggle={(nextShow) => {
          if (!nextShow) {
            if (value.userSpecific !== us) {
              setUs(value.userSpecific)
            }
          }
          setShow(nextShow)
        }}
        menuWrapperClassName='py-2'
      >
        <VariableCurrentListItemBody
          tableId={tableId}
          setShow={setShow}
          columnTypes={variableColumnOptions.columnTypes}
          value={
            multiple
              ? ((contextValue as [])[index] as VariableResultCurrentListItem)
              : (contextValue as VariableResultCurrentListItem)
          }
          onChange={(next) => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : next

            if (multiple) {
              ;(nextVal as DefaultVariableResult[])[index] = next
            }

            onChange(nextVal)
          }}
          setUserSpecific={setUs}
          userSpecific={us}
        />
      </EditDD>
    </Chip>
  )
}

function ChipIcon({
  value: initialValue,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultIcon
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { value: contextValue, onChange, multiple, variableActionOptions } = useVariableSelectContext()
  const [show, setShow] = useState(false)
  const [step, setStep] = useState(1)
  const [value, setValue] = useState(initialValue)
  const action = useMemo(() => {
    return initialValue.actions ? (initialValue.actions as Omit<IActionRepeaterValue, 'id'>).type : undefined
  }, [initialValue])

  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      portalTip
      title={
        action && (
          <div className='flex-c'>
            <div className='text-center whitespace-nowrap'>
              <Trans>Icon</Trans>
            </div>
            <MaterialIcon size={20} icon={initialValue.value} />
            <ArrowShortRight16 />
            <div className='flex-c'>
              {ActionTypeIconMap[action]}
              {getActionLabel(action)}
            </div>
          </div>
        )
      }
    >
      <DragHandler listeners={listeners} />
      <div className='flex-c txt w-full'>
        <MaterialIcon size={20} icon={initialValue.value} />
        <Trans>Icon</Trans>
      </div>
      <EditDD
        show={show}
        onToggle={(nextShow) => {
          setShow(nextShow)
          setStep(1)
        }}
        menuClassName='w-[300px]'
      >
        <VariableIconBody
          value={value}
          onDone={(val) => {
            const nextVal = multiple
              ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
              : val

            if (multiple) {
              ;(nextVal as DefaultVariableResult[])[index] = val
            }

            onChange(nextVal)
          }}
          step={step}
          setStep={setStep}
          setShow={setShow}
          actionType={variableActionOptions.type}
          onChange={setValue}
        />
      </EditDD>
    </Chip>
  )
}

function ChipAvatar({
  value,
  index,
  chipProps: { listeners, ...chipProps },
}: {
  value: VariableResultAvatar
  index: number
  chipProps: ChipProps & { listeners?: SyntheticListenerMap }
}) {
  const { id, value: contextValue, onChange, multiple, variableActionOptions } = useVariableSelectContext()
  const [show, setShow] = useState(false)
  const hasAction = variableActionOptions.type !== VariableActionOption.None

  const action = useMemo(() => {
    return value.actions ? (value.actions as Omit<IActionRepeaterValue, 'id'>).type : undefined
  }, [value.actions])

  return (
    <Chip
      {...chipProps}
      tipClassName='text-m-sm'
      portalTip
      title={
        action && (
          <div className='flex-c'>
            <div className='text-center whitespace-nowrap'>
              <Trans>Avatar</Trans>
            </div>
            <ArrowShortRight16 />
            <div className='flex-c whitespace-nowrap'>
              {ActionTypeIconMap[action]}
              {getActionLabel(action)}
            </div>
          </div>
        )
      }
    >
      <DragHandler listeners={listeners} />
      <div className='w-full'>
        <Trans>Avatar</Trans>
      </div>
      {hasAction && (
        <EditDD
          show={show}
          onToggle={(nextShow) => {
            setShow(nextShow)
          }}
          menuClassName='w-[300px]'
        >
          <VariableAvatarBody
            setShow={setShow}
            id={id}
            action={variableActionOptions.type}
            value={value}
            onDone={(val) => {
              const nextVal = multiple
                ? ([...((contextValue as DefaultVariableResult[]) ?? [])] as DefaultVariableResult[])
                : val

              if (multiple) {
                ;(nextVal as DefaultVariableResult[])[index] = val
              }
              onChange(nextVal)
            }}
          />
        </EditDD>
      )}
    </Chip>
  )
}

function VariableChip({ value, index }: { index: number; value: DefaultVariableResult }) {
  const {
    id,
    value: contextValue,
    onChange,
    multiple,
    inputRef,
    variableColumnOptions,
    variableCustomTextOptions,
    disableInput,
  } = useVariableSelectContext()

  const fileTypes: FileCategory[] = getFileTypeFromId(id)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: multiple ? (contextValue as DefaultVariableResult[])[index].id : '',
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const chipProps: ChipProps & { listeners?: SyntheticListenerMap } = {
    defaultSize: false,
    className: clsx('text-m-sm min-h-[1.5rem]', multiple && 'w-[137px]! txt'),
    listeners,
    removeClassName: 'text-primary-500',
    onRemove: () => {
      return multiple
        ? onChange(
            (contextValue as []).filter((v, i) => {
              return i !== index
            })
          )
        : onChange({
            id: uuid4(),
            value: '',
            flag: VariableSelectFeatureFlag.CustomText,
          })
    },
  }
  const { t } = useTranslation()

  useEffect(() => {
    if (!multiple && variableCustomTextOptions.validation && value.flag === VariableSelectFeatureFlag.CustomText) {
      if (value.value) {
        variableCustomTextOptions.validation(value.value as string)
      }
    }
  }, [value, variableCustomTextOptions, multiple])

  const ctxValue = (contextValue as DefaultVariableResult as VariableResultCommon)?.value

  const renderContent = () => {
    switch (value?.flag) {
      case VariableSelectFeatureFlag.ColumnData: {
        return <ChipColumnData value={value} index={index} chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.SingleCell: {
        return <ChipSingleCell value={value} index={index} chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.CurrentRow: {
        return <ChipCurrentRow value={value} index={index} chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.CurrentListItem: {
        return <ChipCurrentListItem value={value} index={index} chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.Column: {
        return <ChipColumn value={value} index={index} chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.ProfileData:
        return <ChipProfileData value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.SessionID: {
        return <ChipSessionID chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.UniqueID: {
        return <ChipUniqueID chipProps={chipProps} />
      }
      case VariableSelectFeatureFlag.Icon:
        return <ChipIcon value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.RichText:
        return <ChipRichText value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.File:
        return <ChipFile fileTypes={fileTypes} value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.Image:
        return <ChipImage value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.Menu:
        return <ChipMenu value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.DateTime: {
        return (
          <Chip {...chipProps}>
            <DragHandler listeners={listeners} />
            <div className='w-full'>
              <Trans>{getDateTimeLabel(value.variant)}</Trans>
            </div>
          </Chip>
        )
      }
      case VariableSelectFeatureFlag.CurrentDateTime: {
        return (
          <Chip {...chipProps}>
            <DragHandler listeners={listeners} />
            <div className='w-full'>
              <Trans>Current Date & Time</Trans>
            </div>
          </Chip>
        )
      }
      case VariableSelectFeatureFlag.PageTitle:
        return (
          <Chip {...chipProps}>
            <DragHandler listeners={listeners} />
            <div className='w-full'>
              <Trans>Page Title</Trans>
            </div>
          </Chip>
        )
      case VariableSelectFeatureFlag.PageCover:
        return <ChipPageCover value={value as any} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.SearchBox:
        return (
          <Chip {...chipProps}>
            <DragHandler listeners={listeners} />
            <div className='w-full'>
              <Trans>Search Box</Trans>
            </div>
          </Chip>
        )
      case VariableSelectFeatureFlag.Space:
        return <ChipSpace chipProps={chipProps} />
      case VariableSelectFeatureFlag.Avatar:
        return <ChipAvatar value={value} index={index} chipProps={chipProps} />
      case VariableSelectFeatureFlag.CustomText:
        const { inputType, placeholder } = variableCustomTextOptions
        const isDate = inputType === 'datetime'

        return multiple ? (
          <ChipCustomText value={value} index={index} chipProps={chipProps} />
        ) : isDate ? (
          <DateTimeInput
            value={ctxValue}
            onChange={(date) => {
              onChange?.({
                id: uuid4(),
                value: date || '',
                flag: VariableSelectFeatureFlag.CustomText,
              })
            }}
            menuClassName='z-[301]'
            showTime
          />
        ) : (
          <ChipTextInput
            id={`${id}-custom-input`}
            ref={inputRef}
            disabled={disableInput}
            value={ctxValue ?? ''}
            placeholder={placeholder ? t(placeholder) : inputType === 'number' ? t('Enter value') : t('Enter value')}
            onChange={(val) => {
              const id = (contextValue as DefaultVariableResult).id || uuid4()
              if (inputType === 'number') {
                const vv = val.replace(/[^\d.\-]/g, '')
                let finalVal = undefined

                if (vv === '') {
                  onChange?.({
                    id,
                    value: '',
                    flag: VariableSelectFeatureFlag.CustomText,
                  })
                  return
                }

                if (vv === '-') {
                  finalVal = '-'
                } else {
                  const numV = Number(vv)
                  if (!isNaN(numV)) {
                    finalVal = numV
                  }
                  if (vv.endsWith('.')) {
                    finalVal = vv
                  }
                }

                onChange?.({
                  id,
                  value: typeof finalVal !== 'undefined' ? finalVal.toString() : '',
                  flag: VariableSelectFeatureFlag.CustomText,
                })
              } else {
                onChange?.({
                  id,
                  value: val,
                  flag: VariableSelectFeatureFlag.CustomText,
                })
              }
            }}
          />
        )
      case VariableSelectFeatureFlag.ActionOutput: {
        return (
          <Chip {...chipProps}>
            <DragHandler listeners={listeners} />
            <div className='w-full'>
              <Trans>Action Output</Trans>
            </div>
          </Chip>
        )
      }
      case VariableSelectFeatureFlag.GlobalVariable:
        return <ChipGlobalVariable value={value} chipProps={chipProps} />
    }
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {renderContent()}
    </div>
  )
}

export default VariableChip
